// Adapted from Vercel AI Elements (https://ai-sdk.dev/elements), Apache-2.0.
// Uses framer-motion (already in the admin app) instead of `motion`.
import { cn } from '@/lib/utils'
import { motion, useReducedMotion } from 'framer-motion'
import { type CSSProperties, memo } from 'react'

export interface ShimmerProps {
  children: string
  className?: string
  duration?: number
  spread?: number
}

/** Text with a light sweeping across it: the "working on it" look. */
export const Shimmer = memo(
  ({ children, className, duration = 2, spread = 2 }: ShimmerProps) => {
    const reduce = useReducedMotion()
    if (reduce)
      return (
        <span
          className={cn('text-neutral-500 dark:text-neutral-400', className)}
        >
          {children}
        </span>
      )
    return (
      <motion.span
        animate={{ backgroundPosition: '0% center' }}
        initial={{ backgroundPosition: '100% center' }}
        transition={{
          repeat: Number.POSITIVE_INFINITY,
          duration,
          ease: 'linear'
        }}
        className={cn(
          'relative inline-block bg-[length:250%_100%,auto] bg-clip-text text-transparent',
          '[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--color-background),#0000_calc(50%+var(--spread)))] [background-repeat:no-repeat,padding-box]',
          className
        )}
        style={
          {
            '--spread': `${children.length * spread}px`,
            backgroundImage:
              'var(--bg), linear-gradient(var(--color-muted-foreground), var(--color-muted-foreground))'
          } as CSSProperties
        }
      >
        {children}
      </motion.span>
    )
  }
)
Shimmer.displayName = 'Shimmer'
